import { helpers, required, requiredIf } from "@vuelidate/validators";
import {ENTITY_TYPES} from "@/utils/constants.js";

// Regex
export const PBM_REGEX = /^PBM\d{3}$/;
export const NAIC_REGEX = /^\d{5}$/;

// Type helpers
export const isPBM = (_value, vm) => vm?.type === ENTITY_TYPES.PBM;
export const isNAIC = (_value, vm) => vm?.type && vm.type !== ENTITY_TYPES.PBM;

// Uniqueness helper for pbmId / naicId
// Now accepts an optional `propName` so callers can request checking a specific property
export const makeUniqueChecker = (existingEntities, propName) =>
    helpers.withMessage(
        "This ID is already assigned to another entity",
        (value, vm) => {
            // No value to check => valid
            if (!value) return true;

            // Decide which property to check: explicit propName wins, otherwise derive from vm.type
            const prop = propName || (vm?.type === ENTITY_TYPES.PBM ? "pbmId" : "naicId");

            // Only validate pbmId uniqueness for PBM entities, and naicId uniqueness for non-PBM entities
            if (prop === 'pbmId' && vm?.type !== ENTITY_TYPES.PBM) return true;
            if (prop === 'naicId' && vm?.type === ENTITY_TYPES.PBM) return true;

            const duplicate = existingEntities.find(e => e[prop] === value);

            // If editing an existing entity, allow matching IDs for the same entity
            return !duplicate || String(duplicate.id) === String(vm?.id);
        }
    );

// Shared name/type/status rules
export const baseEntityRules = {
    name: {
        required: helpers.withMessage("Entity Name is required", required)
    },
    type: {
        required: helpers.withMessage("Entity Type is required", required)
    },
    status: {
        required: helpers.withMessage("Entity Status is required", required)
    }
};

export const pbmIdRules = (existingEntities) => ({
    required: helpers.withMessage(
        "PBM ID is required for type PBM",
        requiredIf(isPBM)
    ),
    format: helpers.withMessage(
        'PBM ID must be "PBM" followed by 3 digits (e.g., PBM123)',
        helpers.regex(PBM_REGEX)
    ),
    unique: makeUniqueChecker(existingEntities, "pbmId")
});

export const naicIdRules = (existingEntities) => ({
    required: helpers.withMessage(
        "NAIC ID is required for this entity type",
        requiredIf(isNAIC)
    ),
    format: helpers.withMessage(
        "NAIC ID must be exactly 5 digits",
        helpers.regex(NAIC_REGEX)
    ),
    unique: makeUniqueChecker(existingEntities, "naicId")
});